// schedules-api.ts — C7: typed fetch wrappers for /api/schedules/*.
//
// Used only by SchedulesPanel. Bearer auth via getStoredToken(), same shape as
// BrainStrip's /api/usage fetch. Every non-2xx becomes a ScheduleApiError so
// the panel renders the gateway's own honest error code.

import { getStoredToken } from "../../hooks/useAuth"
import type {
  ScheduleKillSwitchState,
  ScheduleListView,
  ScheduleRow,
  ScheduleRun,
} from "../../shared/schedules"

export class ScheduleApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = "ScheduleApiError"
    this.status = status
    this.code = code
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken()
  const res = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  })

  if (!res.ok) {
    let code = `http_${res.status}`
    let message = res.statusText
    try {
      const body = (await res.json()) as { error?: string; message?: string }
      if (body.error) code = body.error
      if (body.message) message = body.message
    } catch {
      // Non-JSON error body — keep the status text.
    }
    throw new ScheduleApiError(res.status, code, message)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export function fetchSchedules(fresh = false): Promise<ScheduleListView> {
  return call<ScheduleListView>(`/api/schedules${fresh ? "?fresh=1" : ""}`)
}

export interface CreateScheduleBody {
  identity: string
  kind: string
  name: string
  schedule: string
  schedule_display: string
  prompt: string
  /** Session-bound wakes, slice 2′ — the chat this wake was armed from.
   *  Omit entirely (never `null`) when the schedule is Settings-born. */
  origin_session_id?: string
}

export async function createSchedule(body: CreateScheduleBody): Promise<ScheduleRow> {
  const result = await call<{ schedule: ScheduleRow }>("/api/schedules", {
    method: "POST",
    body: JSON.stringify(body),
  })
  return result.schedule
}

export interface EditScheduleBody {
  name?: string
  schedule?: string
  schedule_display?: string
  prompt?: string
}

export async function editSchedule(id: string, body: EditScheduleBody): Promise<ScheduleRow> {
  const result = await call<{ schedule: ScheduleRow }>(`/api/schedules/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  })
  return result.schedule
}

export function cancelSchedule(id: string): Promise<void> {
  return call<void>(`/api/schedules/${id}`, { method: "DELETE" })
}

export async function actOnSchedule(id: string, action: "arm" | "pause" | "run"): Promise<ScheduleRow> {
  const result = await call<{ schedule: ScheduleRow }>(`/api/schedules/${id}/${action}`, { method: "POST" })
  return result.schedule
}

export async function fetchScheduleRuns(id: string, limit = 10): Promise<ScheduleRun[]> {
  const result = await call<{ runs: ScheduleRun[] }>(`/api/schedules/${id}/runs?limit=${limit}`)
  return result.runs
}

export async function setKillSwitch(on: boolean): Promise<ScheduleKillSwitchState> {
  const result = await call<{ kill_switch: ScheduleKillSwitchState }>("/api/schedules/kill-switch", {
    method: "PUT",
    body: JSON.stringify({ on }),
  })
  return result.kill_switch
}
